// Labels for the sync chip, the staleness warning and the Offline Data sheet.

export const STALE_AFTER_MS = 24 * 60 * 60 * 1000

export const CHIP_TONE = { OK: "ok", BUSY: "busy", OFFLINE: "offline", ERROR: "error" }

export function isStale(lastSyncedAt, now = Date.now()) {
  return Boolean(lastSyncedAt) && now - lastSyncedAt > STALE_AFTER_MS
}

export function formatRelative(timestamp, now = Date.now()) {
  if (!timestamp) return "never"
  const minutes = Math.floor(Math.max(0, now - timestamp) / 60000)
  if (minutes < 1) return "just now"
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

export function formatAsOf(timestamp) {
  if (!timestamp) return ""
  return new Date(timestamp).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  })
}

export function syncChip(sync, { isOnline, now = Date.now() }) {
  if (sync.needsAppUpdate) return { label: "Update the app", tone: CHIP_TONE.ERROR }
  if (sync.phase === "loading" || sync.phase === "syncing") {
    return { label: "Syncing…", tone: CHIP_TONE.BUSY }
  }
  const synced = sync.lastSyncedAt ? `synced ${formatRelative(sync.lastSyncedAt, now)}` : "not synced"
  if (!isOnline) return { label: `Offline · ${synced}`, tone: CHIP_TONE.OFFLINE }
  if (sync.lastError) return { label: `Sync failed · ${synced}`, tone: CHIP_TONE.ERROR }
  if (!sync.hasData) return { label: "Not synced", tone: CHIP_TONE.ERROR }
  return { label: "Up to date", tone: CHIP_TONE.OK }
}

// `cached` is what the device already holds; only used when there is nothing to save ahead,
// which is the case until the image variants exist for this restaurant.
export function photosLabel({ saved, total, state }, cached = null) {
  if (state === "blocked") return "Photos aren't saved for offline use yet"
  if (state === "unsupported") return "Not available on this device"
  if (!total) {
    if (cached) return `${cached} kept as you viewed them`
    return "Kept as you view them"
  }
  if (state === "saving") return `Saving ${saved} of ${total}`
  if (state === "offline") return `${saved} of ${total} saved · waiting for a connection`
  return `${saved} of ${total} saved`
}

// A gzipped response counts decoded bytes against a compressed length, so a percentage
// is only shown when the two agree; otherwise the size downloaded so far is the honest number.
export function downloadFraction(progress) {
  if (!progress?.total || progress.loaded > progress.total) return null
  return Math.min(1, Math.max(0, progress.loaded / progress.total))
}

// How far the menu download has got: a percentage when the size is known, else bytes so far.
export function downloadLabel(progress) {
  if (!progress?.loaded) return "Starting…"
  const fraction = downloadFraction(progress)
  if (fraction === null) return formatBytes(progress.loaded)
  return `${Math.round(fraction * 100)}%`
}

// What the screens say when the first download failed and nothing is saved yet.
export function firstSyncMessage(lastError) {
  if (lastError?.kind === "network") return "Couldn't download the menu. Check the connection and try again."
  if (lastError?.kind === "schema") return "Update the app to load this menu."
  return lastError?.message ?? "Couldn't download the menu. Try again shortly."
}

export function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return "—"
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
